import dayjs from "dayjs";
import { useContext, useRef } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from ".";
import { AppContext } from "../contexts";
import { isProdURL, getLocale } from "../utils";
import Alert from '../components/alert/alert';
import { mergePortalLabels } from '../utilities/stringOverrideDevTools';

const devURL = process.env.REACT_APP_DEV_LAMBDA_URL ? process.env.REACT_APP_DEV_LAMBDA_URL : 'https://customer-portalk8s.devzinier.net/customer-portal';
const prodURL = process.env.REACT_APP_PROD_LAMBDA_URL ? process.env.REACT_APP_PROD_LAMBDA_URL : 'https://customerportal.zinier.net';

const lambda = isProdURL() ? prodURL : devURL;

const IN_PROGRESS = { state: 'inProgress', actionLabelKey: 'cp_details', route: 'task_details' };
const PENDING_REVIEW = { state: 'alert', subTitleKey: 'cp_acknowledgement_needed', actionLabelKey: 'cp_review_report', route: 'task_review' };

// Maps backend task statusValue to how the task is shown in the portal.
// statusKey picks the task's status icon; completed tasks wait for the customer to review their report.
const TASK_STATUS_MAP = {
  workOrderTaskOpen: { ...IN_PROGRESS, statusKey: 'open' },
  workOrderTaskAccepted: { ...IN_PROGRESS, statusKey: 'accepted' },
  workOrderTaskCompleted: { ...PENDING_REVIEW, statusKey: 'completed' },
  workOrderTaskPendingReview: { ...PENDING_REVIEW, statusKey: 'pendingReview' }
};

const mapTask = (task) => {
  const { route, ...config } = TASK_STATUS_MAP[task.statusValue] || TASK_STATUS_MAP.workOrderTaskOpen;
  const steps = task.steps || [];
  const isComplete = steps.length > 0 && steps.every(step => step.isComplete);
  const activeStep = steps.find(step => step.isActive);

  return {
    ...task,
    ...config,
    title: task.taskTypeName || task.title,
    description: task.taskDescription,
    subTitleKey: config.subTitleKey || (isComplete ? 'cp_completed' : activeStep?.title),
    to: `/${route}/${task.id}`
  };
};

export const STORAGE_REF_KEY = 'z-cp-uid';
export const STORAGE_TOKEN_KEY = 'z-cp-token';
const LAMBDA_ACTIONS = {
  GENERATE_OTP: 'generate_otp',
  VALIDATE_OTP: 'validate_otp',
  BOOKING_DETAILS: 'booking_details',
  GET_NEW_LINK: 'get_new_link'
};
const FSE_ACTIONS = {
  FETCH_BOOKING_DETAILS: 'fetchBookingDetails',
  FETCH_AVAILABLE_TIME_SLOTS: 'fetchAvailableTimeSlots',
  RESCHEDULE_BOOKING: 'rescheduleBooking',
  CONFIRM_BOOKING: 'confirmBooking',
  CANCEL_BOOKING: 'cancelBooking',
  CONFIRM_APPOINTMENT: 'userConfirmBooking',
  SAVE_ZONE: 'saveZones',
  SAVE_SMS_CONSENT: 'saveSmsConsent'
};

function useService () {
  const { i18n } = useTranslation();
  const { current: timezoneOffset } = useRef(-(dayjs().utcOffset()))
  const { setBranding, setToken, setLabels, setUid, token, uid, labels: defaultLabels, setAppointmentData, appointmentData, setZones, setSmsConsentDetails, setAppointmentDataLoading, currentWeek, setIs24hrTime : setIs24hrTimeFormat, setDateTimeFormat: setTimeFormatValue, setCurrentWeek, setAppointmentTime, newLinkLoading, setNewLinkLoading, currentMonth, setCurrentMonth, setIsWorkOrderView, setTasks, setWorkOrder, setNeedHelpLink, setLanguages, setAllLabels } = useContext(AppContext);
  const fallbackError = ({ status: false, message: i18n('cp_fallback_error') });
  const navigate = useNavigate();

  const handleErrorFromBooking = (data) => {
    const { labels } = data[0].bookingDetails?.customerResponseData;

    navigate('/message', { state: {
      status: false,
      title: labels['cp_appointment_cancelled'] || i18n('cp_appointment_cancelled'),
      copy:  labels['cp_task_cancelled'] || i18n('cp_task_cancelled'),
    } })
  }

  const handleNetworkFailure = () => {
    navigate('/message', { state: {
      status: false,
      title: i18n('cp_something_went_wrong'),
      copy:  i18n('cp_something_unexpected_happened'),
    } })
  }

  const linkExpiredCheck = (message) => {
    const linkExpiredMessage = 'Link expired. Please request new link.';
    return message === linkExpiredMessage;
  };

  const compileData = ({ status, data, uid, token }) => {
    const {
      appointmentDetails = {},
      steps = [],
      cancelAlowed = false,
      rescheduleAllowed = false,
      mapAllowed = false,
      mapDetails = {},
      cancelAllowedInPortal = false,
      confirmAllowed = true,
      brandingParams,
      googleApiKey,
      helpLine,
      needHelpLink,
      isWorkOrderView,
      languages,
      tasks,
      workOrder,
      isSmsConsentRequired,
      isMonthViewEnabled,
      labels = {},
      zones,
      smsConsentDetails,
      defaultDateTime,
      is24hrTime,
      cancelledTask,
      currentSmsConsent
    } = data[0].bookingDetails?.customerResponseData || {};
    const { endTimeInUTC, startTimeInUTC } = appointmentDetails;
    setBranding({ logo: brandingParams?.iconUrl, primaryColor: brandingParams?.colorCode });
    setZones(zones);
    setSmsConsentDetails(smsConsentDetails);
    setUid(uid);
    setToken(token);
    setIsWorkOrderView(isWorkOrderView);
    setLanguages(languages);
    setTasks((tasks || []).map(mapTask));
    setWorkOrder(workOrder || {});
    setNeedHelpLink(needHelpLink || '');
    const { locale } = getLocale();
    const localeLabels = labels[locale] || labels.en || labels;
    const newLabel = mergePortalLabels({
      ...defaultLabels,
      ...localeLabels
    });
    setAllLabels(labels);
    setLabels(newLabel);
    if(is24hrTime === "false"){
      setIs24hrTimeFormat(false);
    }else if(is24hrTime === "true"){
      setIs24hrTimeFormat(true);
    }
   if(defaultDateTime){
    setTimeFormatValue(defaultDateTime)
   }
   setAppointmentTime({ endTimeInUTC, startTimeInUTC })

    return {
      status,
      bookingDetails: {
        ...appointmentDetails,
        confirmationNumber: appointmentDetails.confirmationNumber?.toUpperCase(),
        steps,
        cancelAlowed,
        rescheduleAllowed,
        googleApiKey,
        helpLine,
        needHelpLink,
        isWorkOrderView,
        languages,
        tasks,
        isSmsConsentRequired,
        isMonthViewEnabled,
        mapAllowed,
        confirmAllowed,
        cancelAllowedInPortal,
        origin: mapDetails.bookingLocation,
        destination: mapDetails.technicianLocation,
        cancelledTask,
        newLabel,
        currentSmsConsent
      },
    };
  }

  const fetchFromLambda = async (type, data) => {
    try{
      let response = await fetch(lambda, { 
        method: 'POST',
        headers: { 
          uid: data.uid 
        },
        body: JSON.stringify({
          data: [
            {
              actionType: type,
              locale: navigator.language.toLowerCase(),
              timezoneOffset,
              apiResponseFormat: true,
              apiResponseObject: "customerResponseData",
              ...data,
            }
          ]
        })
      })
      if(!response.ok){
        throw new Error('network error');
      }
      let result = await response.json();
      if (
        !result.data[0]?.status &&
        linkExpiredCheck(result.data[0]?.message)
      ) {
        navigate('/message', {
          state: {
            title: i18n('cp_link_expired'),
            copy: i18n('cp_request_new_access'),
            status: false,
            errorMessage: i18n('request_new_email'),
            newlink: true,
            callBackFunc: () => getNewLink(data.uid),
          },
        });
      } else {
        return result;
      }
      
    }
    catch(error){
      if(type !== 'validate_otp' && type !== 'generate_otp'){
         handleNetworkFailure();
         throw new Error('network error');
      }    
      return fallbackError;
    }
  }

  const sendOtp = async (uid, preferredNotification) => {
    let { data } = await fetchFromLambda(LAMBDA_ACTIONS.GENERATE_OTP, {
      uid,
      preferredNotification
    });

    data =  data?.[0];

    return {
      status: data?.status || false,
      message: data?.message || fallbackError.message,
      otpType: data?.otp_auth_type
    };
  }

  const getNewLink = async (uid) => {
        const request = {
          uid,
          action : LAMBDA_ACTIONS.GET_NEW_LINK
        }
        setNewLinkLoading(true);
        const { data } = await fetchFromLambda(LAMBDA_ACTIONS.GET_NEW_LINK, request);
        const { status } = data?.[0];
        setNewLinkLoading(false);
        if(status){
          navigate('/message', {
            state: {
              title: i18n('cp_new_request_sent'),
              copy:  i18n('cp_new_request_message'),
              status,
              icon : 'successMail'
            }
          });
        }
  }

  const fetchAppointmentDetailsDirectLogin = async (uid) => {
    const request = {
      uid,
      action: FSE_ACTIONS.FETCH_BOOKING_DETAILS,
      confirmationNumber: uid,
    }
    try {
      const { data } = await fetchFromLambda(
        LAMBDA_ACTIONS.BOOKING_DETAILS,
        request
      );
      const { status, token, statuscode } = data?.[0] || {};
      const { customerResponseData } = data?.[0].bookingDetails || {};

      if (status) {
        if (
          !customerResponseData?.status &&
          customerResponseData?.appointmentDetails?.appointmentWindowId
        ) {
          return handleErrorFromBooking(data);
        }
        window.localStorage.setItem(STORAGE_REF_KEY, uid);
        window.localStorage.setItem(STORAGE_TOKEN_KEY, token);
        return compileData({ status, data, uid, token });
      } else {
        return {
          status: false,
          message: data?.[0].message || fallbackError.message,
          statuscode
        };
      }
    } catch (e) {
      throw new Error('network error');
    }
  }

  const verifyAndFetch = async (uid, otp) => {
    const request = {
      uid,
      otp,
      action: FSE_ACTIONS.FETCH_BOOKING_DETAILS,
      confirmationNumber: uid,
    }

    const { data } = await fetchFromLambda(LAMBDA_ACTIONS.VALIDATE_OTP, request);
    const { status, token, statuscode} = data?.[0] || {};
    const { customerResponseData } = data?.[0].bookingDetails || {};

    if (status) {
      if (!customerResponseData?.status && customerResponseData?.appointmentDetails?.appointmentWindowId) {
        return handleErrorFromBooking(data);
      }
      window.localStorage.setItem(STORAGE_REF_KEY, uid);
      window.localStorage.setItem(STORAGE_TOKEN_KEY, token);
      return compileData({ status, data, uid, token});
    } else {
      return {
        status: false,
        message: data?.[0].message || fallbackError.message,
        statuscode
      }
    }
  }

  const fetchBookingDetails = async (startOfWeek, monthWeekEndDate) => {
    const request = {
      uid,
      token,
      confirmationNumber: uid,
      scheduledDate: startOfWeek,
      weekNumber : currentWeek,
      fetchWindowForA: "week",
      monthWeekEndDate,
      action: FSE_ACTIONS.FETCH_AVAILABLE_TIME_SLOTS
    }

    return fetchFromLambda(LAMBDA_ACTIONS.BOOKING_DETAILS, request).then(({ data }) => {
      let items = {};
      let monthWeekData = data?.[0]?.bookingDetails?.customerResponseData?.monthWeekData;
      if(monthWeekData && Object.keys(monthWeekData)?.length > 0){
        items = Object.fromEntries(Object.entries(monthWeekData).sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB)));
      }else {
        items = data?.[0]?.bookingDetails?.customerResponseData?.appointmentWindow;
      }
      // TODO: FSE should send in an array of objects which is not the case, hence workaround?!
      let times = Object.keys(items).sort(function(a, b) {
        return (dayjs(a).isAfter(dayjs(b)) ? 1 : -1)
      }).map(item => items[item]);
      let responseWeekNumber = data?.[0]?.bookingDetails?.customerResponseData?.currentWeek;
      const cancelledTask = data?.[0]?.bookingDetails?.customerResponseData?.cancelledTask;
      if (cancelledTask) {
        window.localStorage.removeItem(STORAGE_REF_KEY);
        window.localStorage.removeItem(STORAGE_TOKEN_KEY);
        setBranding({});
        setAppointmentData({});
        setAppointmentDataLoading(true);
        setToken();
        setUid(undefined);
        setZones(undefined);
        setCurrentWeek(0);
        setIs24hrTimeFormat(false);
        setTimeFormatValue('h:mm a');
        navigate('/message', {
          state: {
            title: i18n('cp_appointment_cancelled'),
            copy:  i18n('cp_task_cancelled'),
            status: false, 
            errorMessage: i18n('cp_task_cancelled'), 
          }
        });
      }
      return { times, responseWeekNumber };
      //return Object.keys(items).map(item => items[item]);
    });
  }

  const fetchBookingDetailsForMonthView = async startOfMonth => { 
    const request = {
      uid,
      token,
      confirmationNumber: uid,
      scheduledDate: startOfMonth,
      monthNumber : currentMonth,
      fetchWindowForA: "month",
      action: FSE_ACTIONS.FETCH_AVAILABLE_TIME_SLOTS
    }

    return fetchFromLambda(LAMBDA_ACTIONS.BOOKING_DETAILS, request).then(({ data }) => {
      const items = data?.[0]?.bookingDetails?.customerResponseData?.appointmentWindow || {};
      // TODO: FSE should send in an array of objects which is not the case, hence workaround?!
      let times = Object.keys(items).sort(function(a, b) {
        return (dayjs(a).isAfter(dayjs(b)) ? 1 : -1)
      }).map(item => items[item]);
      let responseMonthNumber = data?.[0]?.bookingDetails?.customerResponseData?.currentMonth;
      const cancelledTask = data?.[0]?.bookingDetails?.customerResponseData?.cancelledTask;
      if (cancelledTask) {
        window.localStorage.removeItem(STORAGE_REF_KEY);
        window.localStorage.removeItem(STORAGE_TOKEN_KEY);
        setBranding({});
        setAppointmentData({});
        setAppointmentDataLoading(true);
        setToken();
        setUid(undefined);
        setZones(undefined);
        setCurrentMonth(dayjs().valueOf());
        setIs24hrTimeFormat(false);
        setTimeFormatValue('h:mm a');
        navigate('/message', {
          state: {
            title: i18n('cp_appointment_cancelled'),
            copy:  i18n('cp_task_cancelled'),
            status: false, 
            errorMessage: i18n('cp_task_cancelled'), 
          }
        });
      }
      return { times, responseMonthNumber };
    });
  }

  const fetchAppointmentDetails = async () => {
    const request = {
      uid,
      token,
      confirmationNumber: uid,
      action: FSE_ACTIONS.FETCH_BOOKING_DETAILS
    }

    setAppointmentDataLoading(true);
    const { data } = await fetchFromLambda(LAMBDA_ACTIONS.BOOKING_DETAILS, request);
    const { status } = data?.[0]?.status;

    setAppointmentDataLoading(false);
    setAppointmentData(compileData({ status, data, uid, token}).bookingDetails);
  }

  const tryToFetchWithoutLoggingIn = async (uid) => {
    const savedUid = window.localStorage.getItem(STORAGE_REF_KEY);
    const savedToken = window.localStorage.getItem(STORAGE_TOKEN_KEY);
    if (
      !uid ||
      !savedToken ||
      !savedUid ||
      uid !== savedUid
    ) return Promise.resolve({ status: false });

    const request = {
      uid: savedUid,
      token: savedToken,
      action: FSE_ACTIONS.FETCH_BOOKING_DETAILS,
      confirmationNumber: savedUid,
    }

    return fetchFromLambda(LAMBDA_ACTIONS.BOOKING_DETAILS, request).then(({ data }) => {
      const { status } = data?.[0] || {};
      const { customerResponseData } = data?.[0].bookingDetails || {};

      if (!status) return fallbackError;

      if (!customerResponseData.status && customerResponseData.appointmentDetails.appointmentWindowId) {
        handleErrorFromBooking(data);
        return {};
      }

      return compileData({
        status,
        data,
        uid: savedUid,
        token: savedToken
      });
    });
  }

  const confirmBooking = async (time, isReschedule) => {
    //TODO FSE Bug being workaround
    const timeToSendInResponse = { ...time };
    timeToSendInResponse.startTimeInUTC = time.startTimeInUTC.replace('Z', '').replace('T', ' ');
    timeToSendInResponse.endTimeInUTC = time.endTimeInUTC.replace('Z', '').replace('T', ' ');

    const request = {
      uid,
      token,
      confirmationNumber: uid,
      action: isReschedule ? FSE_ACTIONS.RESCHEDULE_BOOKING : FSE_ACTIONS.CONFIRM_BOOKING,
      ...timeToSendInResponse
    }

    return fetchFromLambda(LAMBDA_ACTIONS.BOOKING_DETAILS, request).then(({ data }) => {
      const details = data?.[0]?.bookingDetails?.customerResponseData;
      fetchAppointmentDetails();
      setAppointmentTime({...time})
      setAppointmentData({ ...appointmentData});
      return {
        title: details.responseTitle,
        copy: details.responseMessage,
        status: details.status,
        slotUnavailable : details.slotUnavailable,
        cancelledTask : details.cancelledTask
      }
    });
  }

  const confirmAppointment = async () => {
    const request = {
      uid,
      token,
      confirmationNumber: uid,
      action: FSE_ACTIONS.CONFIRM_APPOINTMENT,
    }

    return fetchFromLambda(LAMBDA_ACTIONS.BOOKING_DETAILS, request).then(({ data }) => {
      const details = data?.[0]?.bookingDetails?.customerResponseData;
      fetchAppointmentDetails();

      return {
        title: details?.responseTitle,
        copy: details?.responseMessage,
        status: details?.status
      }
    });
  }

  const cancelBooking = async () => {
    const request = {
      uid,
      token,
      confirmationNumber: uid,
      action: FSE_ACTIONS.CANCEL_BOOKING,
    }

    return fetchFromLambda(LAMBDA_ACTIONS.BOOKING_DETAILS, request).then(({ data }) => {
      const details = data?.[0]?.bookingDetails?.customerResponseData;

      return {
        title: details.responseTitle,
        copy: details.responseMessage,
        status: details.status
      }
    });
  }

  const saveZone = async (data) => {
    const request = {
      uid,
      token,
      confirmationNumber: uid,
      action: FSE_ACTIONS.SAVE_ZONE,
      zones: data
    }

    return fetchFromLambda(LAMBDA_ACTIONS.BOOKING_DETAILS, request).then(({ data }) => {
      const details = data?.[0]?.bookingDetails?.customerResponseData;
      fetchAppointmentDetails();

      return {
        title: details.responseTitle || 'Data saved successfully',
        copy: details.responseMessage || 'Your data has been saved',
        status: details.status
      }
    });
  }

  const saveSmsConsent = async (data) => {
    try {
      const request = {
        uid,
        token,
        confirmationNumber: uid,
        action: FSE_ACTIONS.SAVE_SMS_CONSENT,
        smsConsentDetails: data
      };
  
      const response = await fetchFromLambda(LAMBDA_ACTIONS.BOOKING_DETAILS, request);
      const details = response?.data?.[0]?.bookingDetails?.customerResponseData;
      fetchAppointmentDetails();
      return {
        title: details?.responseTitle || 'Data saved successfully',
        copy: details?.responseMessage || 'Your data has been saved',
        status: details?.status
      };
    } catch (error) {
      <Alert label={error} />
    }
  };

  return {
    sendOtp,
    verifyAndFetch,
    fetchBookingDetails,
    fetchBookingDetailsForMonthView,
    confirmBooking,
    confirmAppointment,
    cancelBooking,
    tryToFetchWithoutLoggingIn,
    fetchFromLambda,
    saveZone,
    saveSmsConsent,
    fetchAppointmentDetailsDirectLogin,
    getNewLink
  }
}

export default useService;
